import { AlertCircle, Ban, SearchX, RefreshCw } from 'lucide-react';
import { getErrorMessage, isHttpStatus } from '@weebee/api';

interface ErrorStateProps {
  error: unknown;
  title?: string;
  onRetry?: () => void;
}

export function ErrorState({ error, title, onRetry }: ErrorStateProps) {
  const is404 = isHttpStatus(error, 404);
  const is403 = isHttpStatus(error, 403);

  const icon = is404 ? (
    <SearchX className="h-10 w-10 text-muted-foreground" />
  ) : is403 ? (
    <Ban className="h-10 w-10 text-muted-foreground" />
  ) : (
    <AlertCircle className="h-10 w-10 text-destructive" />
  );

  const heading = is404
    ? (title ?? 'Not found')
    : is403
      ? 'Access denied'
      : (title ?? 'Something went wrong');

  const description = is404
    ? 'The resource you requested could not be found.'
    : is403
      ? "You don't have permission to access this resource."
      : getErrorMessage(error);

  const showRetry = !is404 && !is403;

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {icon}
      <p className="mt-4 text-lg font-medium text-foreground">{heading}</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
      {showRetry && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Try again
        </button>
      )}
    </div>
  );
}
